import { startTransition, useEffect } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { BUILD_VERSION_CHECK_INTERVAL } from "../constants";
import { checkVersionCall, getVersionJsonUrl, Log } from "../helpers";
import { clearI18nCache } from "../localization/i18n.cache";
import { IBuildInfo } from "../models";
import {
  buildNeedReloadAtom,
  buildTimestampAtom,
  buildVersionAtom,
} from "../recoil";

export const useVersionCheck = (): void => {
  const [buildVersion, setBuildVersion] = useRecoilState(buildVersionAtom);
  const setBuildTimestamp = useSetRecoilState(buildTimestampAtom);
  const setBuildNeedReload = useSetRecoilState(buildNeedReloadAtom);

  const checkVersion = useEvent(
    async ({
      initial,
      currentVersion,
    }: {
      initial: boolean;
      currentVersion: string | null;
    }) => {
      try {
        const versionCheckUrl = getVersionJsonUrl();

        if (!versionCheckUrl) {
          Log.warn("not found appropriate target endpoint to check version");
          return;
        }

        Log.info(`Checking build version from path ${versionCheckUrl}`);
        const { data } = await checkVersionCall<IBuildInfo>({
          method: "get",
          url: versionCheckUrl,
        });

        if (!data?.buildVersion) {
          Log.warn("not found appropriate data on check version url ");
          return;
        }

        setBuildVersion(data.buildVersion);
        setBuildTimestamp(data.buildTimestamp);

        if (
          !initial &&
          data.buildVersion &&
          currentVersion !== data.buildVersion
        ) {
          // A reload is now queued, so this is the one moment where dropping the cached translation
          // catalogue costs nothing: the reload that follows refetches it. Doing it here means a
          // release that changes translations is picked up by that same reload, rather than leaving
          // the session on the previously cached catalogue.
          clearI18nCache();
          setBuildNeedReload(true);
          Log.info("Version changed, app will reload on next navigation");
        }
      } catch (error) {
        Log.error("Error checking version:", error);
      }
    },
  );

  useEffect(() => {
    if (!buildVersion) {
      startTransition(() => {
        Log.info(`Initial checking build version`);
        checkVersion({ initial: true, currentVersion: buildVersion });
      });
    }

    const intervalId = setInterval(() => {
      startTransition(() => {
        checkVersion({ initial: false, currentVersion: buildVersion });
      });
    }, BUILD_VERSION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkVersion, buildVersion]);
};
